import './style.css'
import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import * as GaussianSplats3D from '@mkkellogg/gaussian-splats-3d'

// ── 场景初始化 ──────────────────────────────────────────────────────────────

const scene = new THREE.Scene()
scene.background = new THREE.Color(0x1a1a2e)

// ── 相机 ───────────────────────────────────────────────────────────────────

const camera = new THREE.PerspectiveCamera(
  60,
  window.innerWidth / window.innerHeight,
  0.1,
  1000
)
camera.position.set(0, 1.5, 4)
camera.up.set(0, 1, 0)
camera.lookAt(0, 0, 0)

// ── 渲染器 ─────────────────────────────────────────────────────────────────

const renderer = new THREE.WebGLRenderer({ antialias: true })
renderer.setSize(window.innerWidth, window.innerHeight)
renderer.setPixelRatio(window.devicePixelRatio)
document.body.appendChild(renderer.domElement)

// ── OrbitControls ──────────────────────────────────────────────────────────

const controls = new OrbitControls(camera, renderer.domElement)
controls.enableDamping = true
controls.dampingFactor = 0.05
controls.minDistance = 0.5
controls.maxDistance = 50
controls.target.set(0, 0, 0)

// ── 3DGS 查看器 ────────────────────────────────────────────────────────────

const loadingEl = document.getElementById('loading')!

const viewer = new GaussianSplats3D.Viewer({
  threeScene: scene,
  camera: camera,
  renderer: renderer,
  selfDrivenMode: false,           // 由我们的 animate 循环驱动
  sharedMemoryForWorkers: false,   // 兼容性更好
  dynamicScene: false,
  freeIntermediateSplatData: true,
})

viewer
  .addSplatScene('https://dorm-model-123-1410265619.cos.ap-guangzhou.myqcloud.com/model.splat', {
    splatAlphaRemovalThreshold: 5,
    showLoadingUI: false,
    position: [0, 0, 0],
    // 四元数 [x, y, z, w]：绕 X 轴旋转 180° 修正坐标系倒置
    // 若仍倒置可改为 [-0.707, 0, 0, 0.707]（绕 X 轴 -90°）
    rotation: [1, 0, 0, 0],
    scale: [1, 1, 1],
  })
  .then(() => {
    loadingEl.style.display = 'none'
    console.log('✅ 3DGS 模型加载完成')
  })
  .catch((err: unknown) => {
    loadingEl.textContent = '❌ 模型加载失败，请确认 /model.splat 文件存在'
    console.error('模型加载失败:', err)
  })

// ── 响应窗口大小 ───────────────────────────────────────────────────────────

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
})

// ── 渲染循环 ───────────────────────────────────────────────────────────────

function animate(): void {
  requestAnimationFrame(animate)
  controls.update()
  viewer.update()
  viewer.render()
}

animate()

// ══════════════════════════════════════════════════════════════════════════
// HUD 数字孪生数据模拟
// ══════════════════════════════════════════════════════════════════════════

/** 在 [min, max] 范围内生成随机浮点数 */
function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min
}

/** 在 [min, max] 范围内生成随机整数 */
function randInt(min: number, max: number): number {
  return Math.floor(rand(min, max + 1))
}

/** 安全地更新元素文本，避免 DOM 未准备好时报错 */
function setText(id: string, text: string): void {
  const el = document.getElementById(id)
  if (el) el.textContent = text
}

/** 安全地设置进度条宽度 */
function setBar(id: string, pct: number): void {
  const el = document.getElementById(id) as HTMLElement | null
  if (el) el.style.width = `${Math.min(100, Math.max(0, pct))}%`
}

// ── HUD 时钟 ───────────────────────────────────────────────────────────────
function updateClock(): void {
  const now = new Date()
  const hh = String(now.getHours()).padStart(2, '0')
  const mm = String(now.getMinutes()).padStart(2, '0')
  const ss = String(now.getSeconds()).padStart(2, '0')
  setText('hud-clock', `${hh}:${mm}:${ss}`)
}

setInterval(updateClock, 1000)
updateClock()

// ── 模拟数据刷新（每 1.5 秒） ─────────────────────────────────────────────
setInterval(() => {
  // [系统监控]
  const cpuTemp = rand(45, 50)
  const gpuTemp = rand(50, 58)
  const memUsage = rand(60, 65)

  setText('cpu-temp', cpuTemp.toFixed(1))
  setText('gpu-temp', gpuTemp.toFixed(1))
  setText('mem-usage', memUsage.toFixed(1))

  // 进度条：CPU/GPU 温度以 100℃ 为满，内存直接用百分比
  setBar('cpu-bar', cpuTemp)
  setBar('gpu-bar', gpuTemp)
  setBar('mem-bar', memUsage)

  // [网络状态]
  const netUp   = rand(1.0, 5.0)
  const netDown = rand(5.0, 15.0)
  const ping    = randInt(10, 30)

  setText('net-up',   netUp.toFixed(1))
  setText('net-down', netDown.toFixed(1))
  setText('net-ping', String(ping))

  // [环境数据]
  const envTemp = rand(23, 26)
  const envHumi = rand(55, 65)
  const pm25    = randInt(25, 45)

  setText('env-temp', envTemp.toFixed(1))
  setText('env-humi', envHumi.toFixed(1))
  setText('env-pm25', String(pm25))

  // PM2.5 进度条（以 150μg/m³ 为满）
  setBar('pm25-bar', (pm25 / 150) * 100)

  // [设备状态]
  const power = randInt(120, 150)
  setText('power-watt', String(power))
  // 进度条（以 300W 为满）
  setBar('power-bar', (power / 300) * 100)

}, 1500)